/* eslint-disable curly */
import {
  screenSizes,
  USER_TYPES,
  EXERCISE_PARAMS_MODES,
  Resource,
  Action,
} from '@/constants/constants';
import { MOVEX_SIDE_TYPE } from '@/constants/movexSideType';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input';
import { useAuth } from '@/zustand/auth-store';
import { useUserType } from '@/zustand/user-type-store';
import { isTherapistRole } from '@/utils/detect-user-type';
import i18n from '@/i18n';
import { localizeNumber, localizeMonth } from '@/utils/i18n-helpers';

const USER_INFO_KEY = 'user-info';

/**
 * Get current user permissions (auth store or user-info from OTP verify).
 * Used by hasPermission and for sidebar / non-React permission checks.
 */
function getPermissions() {
  try {
    const state = useAuth.getState();
    if (
      state.user?.permissions !== undefined &&
      state.user?.permissions !== null &&
      Array.isArray(state.user.permissions)
    ) {
      return state.user.permissions;
    }
  } catch (_) {
    // ignore
  }
  try {
    const raw = localStorage.getItem(USER_INFO_KEY);
    if (!raw) return [];
    const info = JSON.parse(raw);
    return Array.isArray(info?.permissions) ? info.permissions : [];
  } catch (_) {
    return [];
  }
}

// Pass timeZone: 'UTC' for date-only values the backend stores as an instant
// (e.g. expirationDate = end of day UTC), so local offsets don't shift the day.
export function formatDate(value, format = 'DD/MM/YYYY', { timeZone } = {}) {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (isNaN(date.getTime())) {
    return '-';
  }

  // replace all "-" with "/"
  const replaceDashWithSlash = str => str.replace(/-/g, '/');

  if (format === 'MM/DD/YYYY') {
    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      timeZone,
    }).format(date);
  }
  if (format === 'DD/MM/YYYY') {
    return new Intl.DateTimeFormat('en-GB', { timeZone }).format(date);
  }
  if (format === 'YYYY/MM/DD') {
    return replaceDashWithSlash(date.toISOString().slice(0, 10));
  }
  // Fallback: use locale string and replace "-" with "/"
  return replaceDashWithSlash(date.toLocaleDateString());
}

export function getMaxLengthByWindow() {
  const width = window.innerWidth;

  if (width >= screenSizes['2xl']) return 40; // 2xl
  if (width >= screenSizes.xl) return 34; // xl
  if (width >= screenSizes.lg) return 28; // lg
  if (width >= screenSizes.md) return 35; // md
  if (width >= screenSizes.sm) return 35; // sm

  return 25;
}

export function truncateText({ text }) {
  if (typeof text !== 'string') return '';

  const maxLength = getMaxLengthByWindow();

  return text.length <= maxLength ? text : text.slice(0, maxLength) + '...';
}

export function DynamicTruncateText(text, maxChars) {
  if (typeof text !== 'string' || !maxChars) return '';

  if (text.length <= maxChars) return text;

  return `${text.slice(0, maxChars)}...`;
}

export function getPhoneFromSearch(search) {
  const searchWithoutQuestionMark = search.startsWith('?') ? search.slice(1) : search;

  for (const key of ['phone', 'phoneNumber', 'contact']) {
    const param = searchWithoutQuestionMark
      .split('&')
      .find(segment => segment.startsWith(`${key}=`));

    if (!param) {
      continue;
    }

    const value = decodeURIComponent(param.slice(key.length + 1)).trim();

    if (!value) {
      return '';
    }

    return param.includes('=+') && !value.startsWith('+') ? `+${value}` : value;
  }

  return '';
}

export const formatTime = timeInSeconds => {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

export function reduceSeconds(seconds) {
  const total = Number(seconds);
  const lng = i18n.resolvedLanguage || i18n.language || 'en';
  const pickUnit = (base, count) =>
    i18n.t(`unit.${base}_${Number(count) === 1 ? 'one' : 'other'}`, { ns: 'common' });

  if (Number.isNaN(total) || total <= 0) {
    return `${localizeNumber(0, lng)} ${pickUnit('second', 1)}`;
  }

  if (total < 60) {
    const secs = Math.floor(total);
    return `${localizeNumber(secs, lng)} ${pickUnit('sec', secs)}`;
  }

  if (total < 3600) {
    const mins = Math.floor(total / 60);
    return `${localizeNumber(mins, lng)} ${pickUnit('min', mins)}`;
  }

  const hours = Math.floor(total / 3600);
  const remainderSec = total % 3600;
  const mins = Math.floor(remainderSec / 60);
  if (mins > 0) {
    return `${localizeNumber(hours, lng)} ${pickUnit('hr', hours)} ${i18n.t('unit.reduce_and', { ns: 'common' })} ${localizeNumber(mins, lng)} ${pickUnit('min', mins)}`;
  }
  return `${localizeNumber(hours, lng)} ${pickUnit('hr', hours)}`;
}

/** Default seconds per rep when eta/etaPerRep is missing (for ETA calculation). */
const DEFAULT_ETA_PER_REP_SECONDS = 5;

/**
 * Returns total estimated time in seconds for the given exercises.
 * Formulas (non–single-rep, based on reps / based on time):
 * - BILATERAL (default): !holdPerRep → etaPerRep*reps*sets; holdPerRep → (etaPerRep+hold)*reps*sets; time mode → setDuration*sets
 * - UNILATERAL_ALTERNATING, !singleRep && holdPerRep: reps → (hold*2+etaPerRep)*reps*sets; time mode unchanged
 * - UNILATERAL_CONTINUOUS, !singleRep && holdPerRep: same rep formula as bilateral; time mode unchanged
 * - holdPerRep && singleRep: holdDuration*sets (unchanged by side)
 */
export function getTotalTimeSeconds(exercises, formValues = {}) {
  const list = Array.isArray(exercises)
    ? exercises
    : exercises !== undefined && exercises !== null
      ? [exercises]
      : [];
  return list.reduce((total, exercise) => {
    const {
      singleRep = false,
      holdPerRep = false,
      basedOnReps,
      basedOnTime,
      basedOnSingleRep,
      movementName,
      id: exerciseId,
      side: exerciseSide,
    } = exercise;
    const side = exerciseSide ?? MOVEX_SIDE_TYPE.BILATERAL;
    const basedOnRep = exercise.basedOnRep ?? basedOnReps;

    const getValue = (configObj, fieldName, formPrefix) => {
      const identifier = exerciseId || movementName;
      const formFieldName = `${formPrefix}_${identifier}_${fieldName}`;
      if (formValues?.[formFieldName] !== undefined && formValues?.[formFieldName] !== null) {
        return Number(formValues[formFieldName]);
      }
      if (exercise[fieldName] !== undefined && exercise[fieldName] !== null) {
        return Number(exercise[fieldName]);
      }
      if (configObj && configObj[fieldName] !== undefined && configObj[fieldName] !== null) {
        return Number(configObj[fieldName]);
      }
      return 0;
    };

    let time = 0;

    if (!singleRep) {
      const hasTimeConfig =
        basedOnTime &&
        basedOnTime.sets !== undefined &&
        basedOnTime.sets !== null &&
        basedOnTime.setDuration !== undefined &&
        basedOnTime.setDuration !== null;
      const hasRepsConfig =
        (basedOnRep &&
          ((basedOnRep.sets !== undefined && basedOnRep.sets !== null) ||
            (basedOnRep.reps !== undefined && basedOnRep.reps !== null))) ||
        (basedOnReps &&
          ((basedOnReps.sets !== undefined && basedOnReps.sets !== null) ||
            (basedOnReps.reps !== undefined && basedOnReps.reps !== null)));

      // When both configs exist, paramMode decides which to use
      const useRepMode =
        hasRepsConfig &&
        !(hasTimeConfig && exercise.paramMode === EXERCISE_PARAMS_MODES.BASED_ON_TIME);

      if (useRepMode) {
        const sets = getValue(basedOnRep, 'sets', 'basedOnRep') || 1;
        const reps = getValue(basedOnRep, 'reps', 'basedOnRep') || 0;
        const etaPerRepRaw =
          getValue(basedOnRep, 'etaPerRep', 'basedOnRep') ||
          getValue(basedOnRep, 'eta', 'basedOnRep');
        const etaPerRep =
          Number(etaPerRepRaw) > 0 ? Number(etaPerRepRaw) : DEFAULT_ETA_PER_REP_SECONDS;
        const holdDuration = getValue(basedOnRep, 'holdDuration', 'basedOnRep');
        if (!holdDuration) {
          time = etaPerRep * reps * sets;
        } else {
          const holdTerm =
            !singleRep && side === MOVEX_SIDE_TYPE.UNILATERAL_ALTERNATING
              ? holdDuration * 2 + etaPerRep
              : holdDuration + etaPerRep;
          time = holdTerm * reps * sets;
        }
      } else if (hasTimeConfig) {
        const sets = getValue(basedOnTime, 'sets', 'basedOnTime') || 1;
        const setDuration = getValue(basedOnTime, 'setDuration', 'basedOnTime');
        time = setDuration * sets;
      }
      if (time === 0) {
        const setsTop = exercise.sets ?? 0;
        const repsTop = exercise.reps;
        const setDur = exercise.setDuration ?? exercise.time;
        const holdDur = exercise.holdDuration ?? exercise.holdingTime;
        if (repsTop !== undefined && repsTop !== null && setsTop > 0) {
          const etaRaw =
            exercise.etaPerRep ??
            exercise.basedOnRep?.etaPerRep ??
            exercise.basedOnReps?.etaPerRep ??
            exercise.eta;
          const eta =
            etaRaw !== null && etaRaw !== undefined && Number(etaRaw) > 0
              ? Number(etaRaw)
              : DEFAULT_ETA_PER_REP_SECONDS;
          const hold = Number(holdDur) || 0;
          const holdAdd = side === MOVEX_SIDE_TYPE.UNILATERAL_ALTERNATING ? 2 * hold : hold;
          time = (Number(eta) + holdAdd) * Number(repsTop) * setsTop;
        } else if (
          (setDur !== undefined && setDur !== null) ||
          (holdDur !== undefined && holdDur !== null)
        ) {
          const sd = Number(setDur) || 0;
          const hd = Number(holdDur) || 0;
          time = setsTop * (hd > 0 ? hd : sd);
        }
      }
    } else if (holdPerRep && (basedOnSingleRep || exercise.basedOnSingleRep)) {
      const single = exercise.basedOnSingleRep ?? basedOnSingleRep;
      const sets = getValue(single, 'sets', 'basedOnSingleRep') || 1;
      const holdDuration = getValue(single, 'holdDuration', 'basedOnSingleRep');
      time = holdDuration * sets;
    }

    return total + time;
  }, 0);
}

export function calculateTotalTime(exercises, formValues = {}) {
  const totalSeconds = getTotalTimeSeconds(exercises, formValues);
  return reduceSeconds(totalSeconds);
}

export function capitalize(text, capitalizeFirst = true, language = 'en') {
  if (typeof text !== 'string' || !text) {
    return text;
  }

  // Check if current language is English
  if (language === 'en' || language.startsWith('en-')) {
    return text
      .split(' ')
      .map(word => {
        if (capitalizeFirst) {
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        } else {
          return word.charAt(0).toLowerCase() + word.slice(1).toLowerCase();
        }
      })
      .join(' ');
  }

  // Return original text for non-English languages
  return text;
}
export const globalDateFormat = date => {
  if (!date) return '';

  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj)) return '';

  const day = localizeNumber(dateObj.getDate());
  const year = localizeNumber(dateObj.getFullYear());

  // Arabic reads numeric DD/MM/YYYY - a month name with day/year runs reads
  // out of order under RTL ("أكتوبر 5 2026").
  if (i18n.language === 'ar') {
    const pad = n => localizeNumber(String(n).padStart(2, '0'));
    return `${pad(dateObj.getDate())}/${pad(dateObj.getMonth() + 1)}/${year}`;
  }

  const month = localizeMonth(dateObj);
  return `${month} ${day} ${year}`;
};
export const formatOtpTime = seconds => {
  if (seconds > 60) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}${t('unit.m', { ns: 'common' })} ${secs}${t('unit.s', { ns: 'common' })}`;
  }
  return `${seconds}s`;
};

export function cutTextPart(sentence, cutText) {
  if (!sentence || !cutText) return sentence;

  // Case-insensitive replace & trim extra spaces
  const result = sentence.replace(new RegExp(cutText, 'gi'), '').trim();

  // Remove double spaces caused by the cut
  return result.replace(/\s+/g, ' ');
}

export const getCountryByCallingCode = code => {
  const countries = getCountries();
  return countries.find(c => getCountryCallingCode(c) === String(code));
};

/**
 * Build E.164 phone string from country code and national number; normalize with libphonenumber when valid.
 * @param {string} countryCode - ISO country code (e.g. 'EG', 'US')
 * @param {string} nationalNumber - National number digits (no country code)
 * @returns {string} E.164 string (e.g. '+201234567890') or empty string
 */
export const formatPhoneToE164 = (countryCode, nationalNumber) => {
  if (!nationalNumber) return '';
  const rawE164 = `+${getCountryCallingCode(countryCode)}${nationalNumber}`;
  const parsed = parsePhoneNumberFromString(rawE164);
  return (parsed?.isValid() ? parsed.format('E.164') : rawE164) ?? '';
};

export const parseFullPhoneNumber = (full, fieldName = 'phoneNumber') => {
  let phoneCh = '';
  let phoneNumber = '';
  let isoCountry = '';

  if (!full) {
    return {
      [fieldName]: '',
      phoneCh,
      isoCountry,
    };
  }

  const parsed = parsePhoneNumberFromString(full);

  if (parsed) {
    phoneCh = parsed.countryCallingCode;
    phoneNumber = parsed.nationalNumber;
    isoCountry = parsed.country || '';
  } else {
    const digits = full.replace(/\D/g, '');
    phoneCh = digits.slice(0, 3);
    phoneNumber = digits;
  }

  return {
    [fieldName]: phoneNumber,
    phoneCh,
    isoCountry,
  };
};

export function monthViewRowCountUTC(date, weekStartsOn = 0) {
  const MS_PER_DAY = 24 * 60 * 60 * 1000;

  function atUTC(y, m, d) {
    return Date.UTC(y, m, d);
  }

  function dayOfWeekUTC(msUtc) {
    return new Date(msUtc).getUTCDay();
  }

  const y = date.getFullYear();
  const m = date.getMonth();

  const firstMs = atUTC(y, m, 1);
  const lastMs = atUTC(y, m + 1, 0);

  function startOfWeekMs(ms) {
    const dow = dayOfWeekUTC(ms);
    const diff = (dow - weekStartsOn + 7) % 7;
    return ms - diff * MS_PER_DAY;
  }
  function endOfWeekMs(ms) {
    const dow = dayOfWeekUTC(ms);
    const diff = (weekStartsOn + 6 - dow + 7) % 7;
    return ms + diff * MS_PER_DAY;
  }

  const firstVisible = startOfWeekMs(firstMs);
  const lastVisible = endOfWeekMs(lastMs);

  const visibleDays = Math.round((lastVisible - firstVisible) / MS_PER_DAY) + 1;
  return visibleDays / 7;
}

export function formatFullDate(date) {
  if (!(date instanceof Date) || isNaN(date)) {
    throw new Error('Invalid date object');
  }

  return date.toLocaleDateString('en-US', {
    weekday: 'long', // Sunday
    year: 'numeric', // 2025
    month: 'long', // June
    day: 'numeric', // 1
  });
}

export function formatDateOnly(date) {
  if (date === null || date === undefined) return null;

  const d = date instanceof Date ? new Date(date.getTime()) : new Date(date);
  if (isNaN(d.getTime())) return null;

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Returns the current week number of a prescription that started on `startDate`
 * (1-indexed, e.g. day 1-7 of treatment = week 1). Used to floor the duration
 * field so a prescription can't be shortened to fewer weeks than have already elapsed.
 */
export function getCurrentWeekNumber(startDate) {
  if (!startDate) return 1;
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  if (isNaN(start.getTime())) return 1;

  const diffDays = Math.floor((Date.now() - start.getTime()) / (24 * 60 * 60 * 1000));
  if (diffDays < 0) return 1;

  return Math.floor(diffDays / 7) + 1;
}

/**
 * Returns a zero-padded display ID for a table row based on pagination.
 * Use in table column cells to show sequential numbers (e.g. 01, 02, 11, 12) that respect page and page size.
 *
 * @param {Object} params - Parameters for the display ID calculation.
 * @param {number} [params.pageIndex=0] - Current page index (0-based).
 * @param {number} [params.pageSize=10] - Number of rows per page.
 * @param {number} params.rowIndex - Row index within the current page (0-based).
 * @param {number} [params.minDigits=2] - Minimum number of digits (zero-padded). Default: 2.
 * @returns {string} Zero-padded display ID (e.g. "01", "02", "11", "12").
 *
 * @example
 * // Page 1, 5 rows: "01", "02", "03", "04", "05"
 * getTableDisplayId({ pageIndex: 0, pageSize: 5, rowIndex: 0 }); // "01"
 * getTableDisplayId({ pageIndex: 0, pageSize: 5, rowIndex: 4 }); // "05"
 *
 * // Page 2, 10 rows per page: "11", "12", ..., "20"
 * getTableDisplayId({ pageIndex: 1, pageSize: 10, rowIndex: 0 }); // "11"
 * getTableDisplayId({ pageIndex: 1, pageSize: 10, rowIndex: 9 }); // "20"
 *
 * // In a table column cell:
 * cell: info => {
 *   const displayId = getTableDisplayId({
 *     pageIndex: state.pageIndex ?? 0,
 *     pageSize: state.pageSize ?? 10,
 *     rowIndex: info.row.index,
 *   });
 *   return <NameCell value={displayId} ... />;
 * }
 */
export function getTableDisplayId({
  pageIndex = 0,
  pageSize = 10,
  rowIndex = 0,
  minDigits = 2,
} = {}) {
  const globalIndex = pageIndex * pageSize + rowIndex + 1;
  return String(globalIndex).padStart(minDigits, '0');
}

export const encodeId = id => {
  if (id === null || id === undefined) return null;

  return btoa(String(id)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const decodeId = encoded => {
  if (!encoded) return null;

  let base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');

  // restore padding
  while (base64.length % 4) {
    base64 += '=';
  }

  try {
    return atob(base64);
  } catch {
    // URL may contain raw id (e.g. from old links or missing encodeId)
    return String(encoded);
  }
};

// Helper function to get ordinal suffix
export const getOrdinalSuffix = day => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

export const getLoginPath = () => {
  const userType = localStorage.getItem('user-type');
  if (userType === USER_TYPES.DASHBOARD) return '/login';
  return '/login';
};

// If the 'user-type' key is missing/stale but we still hold an authenticated
// user (valid accessToken), recover the type from the user's role instead of
// falling through to login - otherwise an authenticated patient can get stuck
// bouncing between getHomePath() and the login page's own redirect (see
// getHomePath below and PublicRoute).
const inferUserTypeFromAuthUser = () => {
  try {
    const role = useAuth.getState()?.user?.role;
    const roleName = (typeof role === 'string' ? role : role?.name) || '';
    const normalized = roleName.toLowerCase();
    if (normalized === 'patient') return USER_TYPES.PATIENT;
    if (normalized === 'admin') return USER_TYPES.DASHBOARD;
    if (normalized === 'clinic owner') return USER_TYPES.CLINIC_OWNER;
    if (isTherapistRole(normalized)) return USER_TYPES.THERAPIST;
    if (normalized === 'medical director') return USER_TYPES.MEDICAL_DIRECTOR;
    if (normalized === 'receptionist') return USER_TYPES.RECEPTIONIST;
  } catch (_) {
    // ignore
  }
  return null;
};

const KNOWN_USER_TYPES = [
  USER_TYPES.PATIENT,
  USER_TYPES.DASHBOARD,
  USER_TYPES.THERAPIST,
  USER_TYPES.CLINIC_OWNER,
  USER_TYPES.MEDICAL_DIRECTOR,
  USER_TYPES.RECEPTIONIST,
];

export const getHomePath = () => {
  let userType = localStorage.getItem('user-type');
  if (!KNOWN_USER_TYPES.includes(userType)) {
    const inferred = inferUserTypeFromAuthUser();
    if (inferred) {
      userType = inferred;
      try {
        localStorage.setItem('user-type', inferred);
      } catch (_) {
        // ignore
      }
    }
  }
  if (userType === USER_TYPES.PATIENT) return '/patients/home';
  if (userType === USER_TYPES.DASHBOARD) return '/dashboard/physiotherapists';
  // Clinic Owner always lands on the onboarding gate first - it reads
  // GET /onboarding/status itself and forwards to /overview when there's no
  // active flow (not their first login).
  if (userType === USER_TYPES.CLINIC_OWNER) return '/clinic-setup';
  // Every staff role starts on its overview. Medical Director and
  // Receptionist have no onboarding flow, so they go straight there.
  if (userType === USER_TYPES.MEDICAL_DIRECTOR) return '/overview';
  if (userType === USER_TYPES.RECEPTIONIST) return '/overview';
  // Therapist always lands on the activation gate first - it reads
  // GET /onboarding/status itself and forwards to /overview when
  // there's no active flow (not their first login) - see useTherapistOnboarding.
  if (userType === USER_TYPES.THERAPIST) return '/therapist-onboarded';
  // Unknown/missing user-type means we can't tell which home page is safe to
  // send this user to - send them to login instead of guessing therapist and
  // landing a patient on a permission-denied exercise-library page.
  return getLoginPath();
};

export const mergedRefs = (...refs) => {
  return node => {
    refs.forEach(ref => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    });
  };
};

/**
 * Check if the current user has a permission. Use in sidebar, routes, or any non-React code.
 * Permissions come from OTP verify response (auth store / user-info). Shape: { id, action, subject }.
 *
 * @param {Object} params
 * @param {string} params.subject - Resource/entity (e.g. "Patient", "Prescription")
 * @param {string} params.action - Operation (e.g. "create", "read", "update", "delete")
 * @returns {boolean}
 *
 * @example
 * if (hasPermission({ subject: 'Patient', action: 'create' })) { ... }
 * // Sidebar: show item only when allowed
 * items.filter(item => hasPermission({ subject: item.subject, action: item.action }))
 */
export function hasPermission({ subject, action }) {
  if (subject === undefined || subject === null || action === undefined || action === null) {
    return false;
  }
  const permissions = getPermissions();
  return permissions.some(perm => perm.subject === subject && perm.action === action);
}

// A Medical Director can't work with a patient's prescriptions or visit history
// (assigning one lands on a 403), even if the server grants the VIEW
// permission - so the Prescription / Appointments tabs and the insights card
// are hidden for them.
const isMedicalDirector = () => useUserType.getState().userType === USER_TYPES.MEDICAL_DIRECTOR;

export const canViewPatientPrescriptions = () =>
  hasPermission({ subject: Resource.PRESCRIPTION, action: Action.VIEW }) && !isMedicalDirector();

export const canViewPatientVisitHistory = () =>
  hasPermission({ subject: Resource.VISIT_NOTE, action: Action.VIEW }) && !isMedicalDirector();
